import React, {useMemo, useState} from "react";
import {Add, Build, Delete, Edit, Storage} from "@mui/icons-material";
import {Box, Button, DialogActions, DialogContent, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {isDataportalNode} from "../../constants/nodes";
import AlertDialog from "../alert-dialog";
import ConfirmationDialog from "../confirmation-dialog";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import ResourceSettings from "./ResourceSettings";
import {
  deleteEtlResource,
  deleteResource,
  getSupersetDependencies,
  hideResourceList,
  showEtlWizardModal,
  showResource
} from "../../state/package-manager/packageManagerActions";
import {packageManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {
  CATALOG_RESOURCE_TYPE_7Z,
  CATALOG_RESOURCE_TYPE_CSV,
  CATALOG_RESOURCE_TYPE_GZ,
  CATALOG_RESOURCE_TYPE_GZIP,
  CATALOG_RESOURCE_TYPE_ZIP
} from "../../utils/catalog";
import EXTRAS from "../../utils/extras";
import {canImportData, getIsUserSuperadmin} from "../../utils/permissions";

const EXCLUDED_RESOURCE_TYPE = ["ExportableMetadata"];

const ETL_ALLOWED_RESOURCE_TYPES = [
  CATALOG_RESOURCE_TYPE_CSV,
  CATALOG_RESOURCE_TYPE_ZIP,
  CATALOG_RESOURCE_TYPE_GZ,
  CATALOG_RESOURCE_TYPE_GZIP,
  CATALOG_RESOURCE_TYPE_7Z
];

const SupersetDepsList = ({deps}) => {
  const {t} = useTranslation();

  const charts = deps?.Charts || [];
  const dashboards = deps?.Dashboards || [];

  if (charts.length === 0 && dashboards.length === 0) return null;

  return (
    <>
      <Box className="resource-list-table__superset-used__deps-list">
        {charts.map((name, idx) => (
          <Typography key={`chart-${idx}`}>
            {`- ${name} (${t("modules.packageManager.resourceList.dialog.supersetUsed.content.chart")})`}
          </Typography>
        ))}
        {dashboards.map((name, idx) => (
          <Typography key={`dashboard-${idx}`}>
            {`- ${name} (${t("modules.packageManager.resourceList.dialog.supersetUsed.content.dashboard")})`}
          </Typography>
        ))}
      </Box>
      <br />
    </>
  );
};

const ResourceList = ({node}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(state => state.user);

  const {readOnly, isResourceListModalVisible, nodeId, packages, packageId} = useSelector(packageManagerSelector);

  const selectedPackage = useMemo(() => (packages || []).find(({id}) => id === packageId), [packages, packageId]);

  const isOwner = useMemo(
    () => getIsUserSuperadmin(user) || !selectedPackage?.owner || selectedPackage?.owner === user.userId,
    [selectedPackage, user]
  );

  const resources = useMemo(() => {
    const resources = selectedPackage
      ? (selectedPackage.resources || [])
          .filter(resource => !resource.isLinkedResource && !EXCLUDED_RESOURCE_TYPE.includes(resource.type))
          .map(resource => ({
            ...resource,
            linkedResources: selectedPackage?.linkedResources?.[resource.id]
          }))
      : null;
    return resources;
  }, [selectedPackage]);

  const [etlDeleteResId, setEtlDeleteResId] = useState(null);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [isResourceDeleteBlockedAlertOpen, setIsResourceDeleteBlockedAlertOpen] = useState(false);

  const nodeExtras = useMemo(() => EXTRAS.parse(node?.extras), [node]);
  const {SupersetEnabled, EnableDataStore} = nodeExtras;

  const [supersetUsedEditOpen, setSupersetUsedEditOpen] = useState(false);
  const [supersetUsedDeleteOpen, setSupersetUsedDeleteOpen] = useState(false);
  const [supersetResId, setSupersetResId] = useState(null);
  const [supersetDeps, setSupersetDeps] = useState(null);

  const resetSupersetState = () => {
    setSupersetUsedDeleteOpen(false);
    setSupersetUsedEditOpen(false);
    setSupersetDeps(null);
    setSupersetResId(null);
  };

  const handleDeleteEtlResource = resourceId => {
    if (SupersetEnabled) {
      dispatch(
        getSupersetDependencies(
          nodeId,
          resourceId,
          deps => {
            setSupersetUsedDeleteOpen(true);
            setSupersetDeps(deps);
            setSupersetResId(resourceId);
          },
          () => setEtlDeleteResId(resourceId)
        )
      );
    } else {
      setEtlDeleteResId(resourceId);
    }
  };

  const handleEditEtlResource = resourceId => {
    if (SupersetEnabled) {
      dispatch(
        getSupersetDependencies(
          nodeId,
          resourceId,
          deps => {
            setSupersetUsedEditOpen(true);
            setSupersetDeps(deps);
            setSupersetResId(resourceId);
          },
          () => dispatch(showEtlWizardModal(resourceId, true))
        )
      );
    } else {
      dispatch(showEtlWizardModal(resourceId, true));
    }
  };

  return (
    <SettingsDialog
      title={t("modules.packageManager.resourceList.dialog.title", {dataset: selectedPackage?.title})}
      titleProps={{
        icon: <Storage />
      }}
      open={isResourceListModalVisible && resources}
      onClose={() => dispatch(hideResourceList())}
    >
      {resources && (
        <MaterialTable
          id={"resource-list-table"}
          data={resources || []}
          rightActions={
            isOwner && !readOnly
              ? [
                  {
                    id: "resource-list-table__right-actions__create-resource",
                    label: t("modules.packageManager.resourceList.createResource.label"),
                    startIcon: <Add />,
                    onClick: () => dispatch(showResource())
                  }
                ]
              : []
          }
          actions={
            isOwner
              ? [
                  ({type, linkedResources}) =>
                    canImportData(user, nodeId) &&
                    (isDataportalNode(node) || EnableDataStore) &&
                    ETL_ALLOWED_RESOURCE_TYPES.includes(type.toLowerCase())
                      ? {
                          className: "resource-list-table__right-actions__etl-dropdown",
                          icon: <Build />,
                          isDropdown: true,
                          tooltip: t("modules.packageManager.resourceList.etlDropdown.label"),
                          items:
                            (linkedResources || []).length === 0
                              ? [
                                  {
                                    className: "resource-list-table__right-actions__etl-menu-header",
                                    label: t("modules.packageManager.resourceList.etlMenu.label")
                                  },
                                  {
                                    className: "resource-list-table__right-actions__etl-wizard-dialog",
                                    icon: <Add />,
                                    label: t("modules.packageManager.resourceList.etlCreate.label"),
                                    tooltip: t("modules.packageManager.resourceList.etlCreate.label"),
                                    onClick: (_, {id}) => dispatch(showEtlWizardModal(id, false))
                                  }
                                ]
                              : [
                                  {
                                    className: "resource-list-table__right-actions__etl-menu-header",
                                    label: t("modules.packageManager.resourceList.etlMenu.label")
                                  },
                                  {
                                    className: "resource-list-table__right-actions__etl-edit-dialog",
                                    icon: <Edit />,
                                    label: t("modules.packageManager.resourceList.etlEdit.label"),
                                    tooltip: t("modules.packageManager.resourceList.etlEdit.label"),
                                    onClick: (_, {id}) => handleEditEtlResource(id)
                                  },
                                  {
                                    className: "resource-list-table__right-actions__delete-linked-resources",
                                    icon: <Delete />,
                                    label: t("modules.packageManager.resourceList.deleteLinkedResources.label"),
                                    tooltip: t("modules.packageManager.resourceList.deleteLinkedResources.label"),
                                    onClick: (_, {id}) => handleDeleteEtlResource(id)
                                  }
                                ]
                        }
                      : null,
                  ...(!readOnly
                    ? [
                        {
                          className: "resource-list-table__right-actions__edit-resource",
                          icon: <Edit />,
                          tooltip: t("modules.packageManager.resourceList.editResource.label"),
                          onClick: (_, {id}) => dispatch(showResource(id))
                        },
                        {
                          className: "resource-list-table__right-actions__delete-resource",
                          icon: <Delete />,
                          tooltip: t("modules.packageManager.resourceList.deleteResource.label"),
                          onClick: (_, {id, linkedResources}) =>
                            (linkedResources || []).length
                              ? setIsResourceDeleteBlockedAlertOpen(true)
                              : setResourceToDelete(id)
                        }
                      ]
                    : [])
                ]
              : []
          }
          columns={[
            {
              hidden: true,
              field: "id",
              title: t("modules.packageManager.resourceList.columns.id.label"),
              getClassName: () => "resource-list-table__columns__id"
            },
            {
              field: "title",
              title: t("modules.packageManager.resourceList.columns.title.label"),
              getClassName: () => "resource-list-table__columns__title"
            },
            {
              field: "type",
              title: t("modules.packageManager.resourceList.columns.type.label"),
              getClassName: () => "resource-list-table__columns__type"
            }
          ]}
        />
      )}

      <ResourceSettings />

      <ConfirmationDialog
        title={t("modules.packageManager.resourceList.deleteDialog.resource.title")}
        content={t("modules.packageManager.resourceList.deleteDialog.resource.description")}
        open={resourceToDelete}
        onClose={() => setResourceToDelete(null)}
        onConfirm={() => {
          dispatch(deleteResource(nodeId, resourceToDelete));
          setResourceToDelete(null);
        }}
      />
      <ConfirmationDialog
        title={t("modules.packageManager.resourceList.dialog.confirm.title")}
        content={t("modules.packageManager.resourceList.dialog.confirm.description")}
        open={etlDeleteResId}
        onClose={() => setEtlDeleteResId(null)}
        onConfirm={() => {
          dispatch(deleteEtlResource(etlDeleteResId, nodeId));
          setEtlDeleteResId(null);
        }}
      />

      <AlertDialog
        title={t("modules.packageManager.resourceList.dialog.resourceDeleteBlocked.title")}
        open={isResourceDeleteBlockedAlertOpen}
        onClose={() => setIsResourceDeleteBlockedAlertOpen(false)}
      >
        <DialogContent className="resource-list-table__resource-delete-blocked resource-list-table__resource-delete-blocked--delete">
          <Typography>{t("modules.packageManager.resourceList.dialog.resourceDeleteBlocked.content")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResourceDeleteBlockedAlertOpen(false)}>{t("commons.confirm.close")}</Button>
        </DialogActions>
      </AlertDialog>

      <AlertDialog
        title={t("modules.packageManager.resourceList.dialog.supersetUsedEdit.title")}
        open={Boolean(supersetUsedEditOpen)}
        onClose={resetSupersetState}
      >
        <DialogContent className="resource-list-table__superset-used resource-list-table__superset-used--edit">
          <Typography sx={{whiteSpace: "pre-line"}}>
            {t("modules.packageManager.resourceList.dialog.supersetUsedEdit.content.prefix")}
          </Typography>
          <SupersetDepsList deps={supersetDeps} />
          <Typography sx={{whiteSpace: "pre-line"}}>
            {t("modules.packageManager.resourceList.dialog.supersetUsedEdit.content.suffix")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSupersetState}>{t("commons.confirm.close")}</Button>
          <Button
            onClick={() => {
              dispatch(showEtlWizardModal(supersetResId, true));
              resetSupersetState();
            }}
          >
            {t("commons.confirm.confirm")}
          </Button>
        </DialogActions>
      </AlertDialog>

      <AlertDialog
        title={t("modules.packageManager.resourceList.dialog.supersetUsedDelete.title")}
        open={Boolean(supersetUsedDeleteOpen)}
        onClose={resetSupersetState}
      >
        <DialogContent className="resource-list-table__superset-used resource-list-table__superset-used--delete">
          <Typography sx={{whiteSpace: "pre-line"}}>
            {t("modules.packageManager.resourceList.dialog.supersetUsedDelete.content.prefix")}
          </Typography>
          <SupersetDepsList deps={supersetDeps} />
          <Typography sx={{whiteSpace: "pre-line"}}>
            {t("modules.packageManager.resourceList.dialog.supersetUsedDelete.content.suffix")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSupersetState}>{t("commons.confirm.close")}</Button>
          <Button
            onClick={() => {
              dispatch(deleteEtlResource(supersetResId, nodeId));
              resetSupersetState();
            }}
          >
            {t("commons.confirm.confirm")}
          </Button>
        </DialogActions>
      </AlertDialog>
    </SettingsDialog>
  );
};

export default ResourceList;
